import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { APIContext } from '../../Contexts/APIContext';
import ResponsiveTable from '../../Utilities/ResponsiveTable';
import UserManagementRow from './UserManagementRow';
import UserTableRowViewModel from './ViewModels/UserTableRowViewModel';
import UserTableViewModel from './ViewModels/UserTableViewModel';
import Line from '../../Utilities/Line';
import CreateEditUserDialog from './CreateEditUserDialog';
import Button from '@mui/material/Button';
import useDebounce from '../../Utilities/Hooks/UseDebounce';

const tableHeaders: string[] = [
    "Name",
    "Email",
    ""
];

export default function UserManagmentTable() {
    const { get, loading } = React.useContext(APIContext);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [result, setResult] = React.useState<UserTableViewModel>(new UserTableViewModel());
    const [search, setSearch] = React.useState('');
    const [editId, setEditId] = React.useState<string | null>(null);
    const [openCreateEdit, setOpenCreateEdit] = React.useState(false);

    React.useEffect(() => {
        getData();
    }, [page, rowsPerPage, search]);
    React.useEffect(() => {
        if (editId) {
            setOpenCreateEdit(true);
        }
    }, [editId]);

    const getData = async () => {
        get("AdminUser", `GetAdminUsersTable?page=${page}&pageRows=${rowsPerPage}&search=${search}`, setResult);
    };

    const closeEditDialog = (refresh: boolean) => {
        if (refresh) {
            getData();
        }
        setEditId(null);
        setOpenCreateEdit(false);
    };
    const openEditDialog = () => {
        setOpenCreateEdit(true);
    };
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(event.target.value);
        setPage(0);
    };

    const { debouncedFunction: debouncedHandleSearch } = useDebounce(handleSearch, 600);

    return (
        <Grid container component={Paper}>
            <Grid size={12}>
                <Typography variant="h4">Users</Typography>
            </Grid>
            <Line />
            <Grid size={12}>
                <TextField
                    fullWidth
                    color="primary"
                    size="small"
                    variant="outlined"
                    onChange={debouncedHandleSearch}
                    placeholder="Search for user"
                />
            </Grid>
            <Grid>
                <Button variant="contained" onClick={openEditDialog}>
                    Add New Admin
                </Button>
            </Grid>
            <Grid size={12}>
                <TableContainer>
                    <ResponsiveTable>
                        <TableHead>
                            <TableRow>
                                {tableHeaders.map((header: string) => (
                                    <TableCell key={header}>{header}</TableCell>
                                ))}
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody >
                            {(!loading && result) &&
                                result.rows.map((row: UserTableRowViewModel) => {
                                    return (<UserManagementRow key={row.id} row={row} refresh={getData} edit={setEditId} />);
                                })
                            }
                            {loading &&
                                <TableRow
                                    sx={{
                                        height: 53 * rowsPerPage,
                                    }}
                                >
                                    <TableCell colSpan={tableHeaders.length + 1} align="center"><CircularProgress color="primary" /></TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </ResponsiveTable>
                </TableContainer>
                {
                    ((!loading && result)) &&
                    <Grid>
                        <TablePagination
                            rowsPerPageOptions={[15]}
                            component="div"
                            count={result.count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Grid>
                }
            </Grid >
            <CreateEditUserDialog open={openCreateEdit} onClose={closeEditDialog} userId={editId} />
        </Grid >
    );
}
